import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isFreshListVisit } from "@/helpers/FreshListVisit";

// Keeps a list's page, page size, search and filters in the address bar (e.g. /Lots?page=4&size=25&search=ring&sold=false),
// so coming back to the list (Back, Cancel, after saving, a refresh or a shared link) shows the same place.
// A copy is kept per browser tab, so returning through a plain link (e.g. Back / Cancel going to "/Lots") restores the
// last place too. Opening a list from the sidebar (navigation state "freshList") always starts it fresh.

export interface ListFilterDefinition {
    propertyMap: string;
    type?: string;
}

export interface ListPagination {
    currentPage: number;
    pageSize: number;
}

interface Options {
    // The list's filters (the grid's availableFilters): which address values are filters, and how to read them.
    filters?: ListFilterDefinition[];
    // Lists that share a route (e.g. the lots of each auction) are remembered separately.
    scope?: string;
    // Other values the screen keeps with its list but that are not filters (e.g. "tab").
    extras?: string[];
}

const DEFAULT_PAGE_SIZE = 10;
const LIST_PARAMS = ["search", "page", "size"];

const hasValue = (value: any) => value !== undefined && value !== null && value !== "" && value !== "All";

const decode = (raw: string, type?: string) => {
    if (type === "boolean") return raw === "true" ? true : raw === "false" ? false : undefined;
    if (type === "number") {
        const number = Number(raw);
        return raw === "" || isNaN(number) ? undefined : number;
    }
    return raw;
};

const readStorage = (key: string) => {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key: string, value: string) => {
    try {
        if (value) sessionStorage.setItem(key, value);
        else sessionStorage.removeItem(key);
    } catch {
        // Storage unavailable: the address bar still keeps the place.
    }
};

export const useListUrlState = ({ filters = [], scope = "", extras = [] }: Options = {}) => {
    const location = useLocation();
    const navigate = useNavigate();

    const filterKeys = useMemo(() => filters.map(filter => filter.propertyMap), [filters]);
    const extrasKey = extras.join(",");
    const isListParam = useCallback(
        (key: string) => LIST_PARAMS.includes(key) || filterKeys.includes(key) || extrasKey.split(",").includes(key),
        [filterKeys, extrasKey]);
    const storageKey = "pb.list:" + location.pathname.toLowerCase() + (scope ? ":" + scope : "");
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;
    // Kept on the address updates, so the screen is not restarted by its own page / filter changes.
    const stateRef = useRef<any>(location.state);
    stateRef.current = location.state;

    // The starting place (read once): from the address, or, when the address has none, the last place in this tab.
    const initial = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const fromAddress = [...params.keys()].some(isListParam);
        const fresh = isFreshListVisit(location.state);
        let restored = false;

        // Opened from the sidebar: a fresh list, and the last place is forgotten.
        if (!fromAddress && fresh) writeStorage(storageKey, "");

        if (!fromAddress && !fresh) {
            const stored = readStorage(storageKey);
            if (stored) {
                new URLSearchParams(stored).forEach((value, key) => {
                    if (isListParam(key)) {
                        params.set(key, value);
                        restored = true;
                    }
                });
            }
        }

        const startFilters: any = {};
        const search = params.get("search");
        if (search) startFilters.searchQuery = search;
        filters.forEach(filter => {
            const raw = params.get(filter.propertyMap);
            if (raw === null) return;
            const value = decode(raw, filter.type);
            if (hasValue(value)) startFilters[filter.propertyMap] = value;
        });

        const page = Number(params.get("page"));
        const size = Number(params.get("size"));
        const startPagination: ListPagination | undefined = page > 0 || size > 0
            ? { currentPage: page > 0 ? page : 1, pageSize: size > 0 ? size : DEFAULT_PAGE_SIZE }
            : undefined;

        const startExtras: Record<string, string> = {};
        extras.forEach(key => {
            const value = params.get(key);
            if (value !== null) startExtras[key] = value;
        });

        return {
            filters: Object.keys(startFilters).length > 0 ? startFilters : undefined,
            extras: startExtras,
            pagination: startPagination,
            restoredQuery: restored ? params.toString() : undefined,
        };
        // Read once, when the list opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const write = useCallback((apply: (params: URLSearchParams) => void) => {
        const params = new URLSearchParams(window.location.search);
        apply(params);

        const listOnly = new URLSearchParams();
        params.forEach((value, key) => {
            if (isListParam(key)) listOnly.set(key, value);
        });
        writeStorage(storageKey, listOnly.toString());

        const query = params.toString();
        if (("?" + query) === window.location.search || (!query && !window.location.search)) return;
        navigate({ pathname: pathnameRef.current, search: query ? "?" + query : "" }, { replace: true, state: stateRef.current });
    }, [isListParam, navigate, storageKey]);

    // A place restored from this tab's copy is shown in the address bar too; a place opened from the address (a link,
    // a refresh) becomes this tab's copy.
    useEffect(() => {
        if (initial.restoredQuery === undefined) {
            const listOnly = new URLSearchParams();
            new URLSearchParams(window.location.search).forEach((value, key) => {
                if (isListParam(key)) listOnly.set(key, value);
            });
            if (listOnly.toString()) writeStorage(storageKey, listOnly.toString());
        }
        if (initial.restoredQuery !== undefined) {
            const restored = new URLSearchParams(initial.restoredQuery);
            write(params => restored.forEach((value, key) => { if (isListParam(key)) params.set(key, value); }));
        }
        // Once, when the list opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // New search / filters: kept, and the list goes back to its first page.
    const saveFilters = useCallback((selected: any) => {
        write(params => {
            const search = selected?.searchQuery;
            if (hasValue(search)) params.set("search", String(search));
            else params.delete("search");

            filterKeys.forEach(key => {
                const value = selected?.[key];
                if (hasValue(value)) params.set(key, String(value));
                else params.delete(key);
            });

            params.delete("page");
        });
    }, [filterKeys, write]);

    const savePagination = useCallback((pagination: Partial<ListPagination> | null | undefined) => {
        write(params => {
            const page = Number(pagination?.currentPage);
            const size = Number(pagination?.pageSize);
            if (page > 1) params.set("page", String(page));
            else params.delete("page");
            if (size > 0 && size !== DEFAULT_PAGE_SIZE) params.set("size", String(size));
            else params.delete("size");
        });
    }, [write]);

    // Start the list over (search, filters and page), keeping its extras.
    const resetList = useCallback(() => {
        write(params => {
            [...params.keys()].filter(key => LIST_PARAMS.includes(key) || filterKeys.includes(key)).forEach(key => params.delete(key));
        });
    }, [filterKeys, write]);

    // One of the extras changed (e.g. the tab); "restart" also starts the list over.
    const saveExtra = useCallback((key: string, value: string | null | undefined, restart = false) => {
        write(params => {
            if (value === null || value === undefined || value === "") params.delete(key);
            else params.set(key, value);
            if (restart)
                [...params.keys()].filter(param => LIST_PARAMS.includes(param) || filterKeys.includes(param)).forEach(param => params.delete(param));
        });
    }, [filterKeys, write]);

    return {
        initialFilters: initial.filters as any,
        initialPagination: initial.pagination,
        initialExtras: initial.extras,
        saveFilters,
        savePagination,
        resetList,
        saveExtra,
    };
};

export default useListUrlState;
